import { useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

/**
 * A temporary conversation is deleted (with all its files, on the server) as soon as the user leaves it: another page
 * of the app, a reload, a closed tab. While a provider is still working, leaving asks first; leaving anyway stops it
 * and deletes. The server deletes it too once no tab has had it open for a while (a request that never arrived).
 */
export function useTemporaryChat(chatId: string, on: boolean, running: boolean) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { confirm } = useConfirm();
  const busy = useRef(running);
  busy.current = running;
  const gone = useRef(false);
  const leave = () => {
    if (gone.current) return;
    gone.current = true;
    // keepalive: the request still goes out while the page is closing
    void fetch(`/api/chats/${chatId}`, { method: "DELETE", keepalive: true, credentials: "same-origin" }).catch(() => undefined);
    try { localStorage.removeItem(`codvero.draft.chat:${chatId}`); } catch { /* private mode */ }
    qc.removeQueries({ queryKey: ["chat-kind", chatId] });
    qc.removeQueries({ queryKey: ["multi", chatId] });
  };
  useBlocker({
    disabled: !on,
    // the browser's own "leave this page?" while a provider works (a dialog of ours cannot hold a closing tab)
    enableBeforeUnload: () => busy.current,
    shouldBlockFn: async ({ next }) => {
      if (next.pathname === `/chat/${chatId}`) return false; // the same conversation (a panel opened or closed)
      if (busy.current && !(await confirm({ title: t("temporary.leaveTitle"), message: t("temporary.leaveMsg"), confirmLabel: t("temporary.leave"), danger: true }))) return true;
      leave();
      return false;
    },
  });
  useEffect(() => {
    if (!on) return;
    window.addEventListener("pagehide", leave);
    return () => window.removeEventListener("pagehide", leave);
  }, [on, chatId]);
}
